<template>
  <div class="press-releases">
    <div class="press-releases__wrapper">
      <div
        class="press-releases__header-background"
        :style="{
          backgroundImage: `url(${lastPressrelease.header_image})`,
        }"
      ></div>
      <div class="press-releases__main-container">
        <div class="press-releases__details-box">
          <h2 class="press-releases__details-box_title">
            {{ lastPressrelease.title }}
          </h2>
          <span class="press-releases__details-box_data"
            >{{ lastPressrelease.date_published }} | by {{ lastPressrelease.author }}</span
          >
          <span class="press-releases__details-box_details">{{ lastPressrelease.details }}</span>
          <BaseButton
            class="button button--xs button--white"
            text="READ MORE"
            @click="$router.push({ path: `/media/press-releases/${lastPressrelease.slug}` })"
          />
        </div>

        <div v-if="hasPressreleases" class="press-releases__title-box" ref="titleBox">
          <h2 class="press-releases__title-box_title">Press Releases</h2>

          <div class="press-releases__title-box_right">
            <div class="press-releases__title-box_right-content">
              <Tag
                class="tag--public"
                :class="{ 'tag--selected': item.selected }"
                v-for="(item, index) in getVisibleTags"
                :key="`tag-${index}`"
                :value="item.title"
                @click="changeFilterValue(item)"
              />
              <AdditionalInfo
                v-if="getHiddenTags.length"
                class="additional-tags--public"
                :items="getHiddenTags"
                :parentClientRect="tagsClientRect"
                :isPublic="true"
                @getParentClientRect="getParentClientRect"
                @changeFilterValue="changeFilterValue"
              ></AdditionalInfo>
            </div>
            <div class="divider divider--vertical"></div>
            <div class="press-releases__title-box_right-content">
              <PublicSelect
                :items="sortData"
                :selected="cardData.sort"
                @input="sortPressreleases($event.id)"
              ></PublicSelect>
            </div>
          </div>
        </div>
        <PressReleasesList
          v-if="
            hasPressreleases && Array.isArray(pressreleasesList) && pressreleasesList.length > 0
          "
          :pressreleasesList="pressreleasesList"
        />
        <h6 class="press-releases__empty" v-else>No Press releases</h6>

        <div v-if="showLoadMoreButton && hasMorePages" class="press-releases__button-box">
          <BaseButton
            class="button button--xs button--white"
            text="LOAD MORE"
            @click.native="handleLoadMoreClick"
          />
        </div>
      </div>
    </div>
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import PublicSelect from "@/components/inputs/PublicSelect.vue";
import Tag from "@/components/Tag.vue";
import AdditionalInfo from "@/components/AdditionalInfo.vue";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import PressReleasesList from "@/views/public/library/pressreleases/components/PressReleasesList.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: { BaseButton, PublicSelect, Tag, AdditionalInfo, PressReleasesList },

  data() {
    return {
      tagsClientRect: { bottom: 0, right: 0 },
      sortData: [
        { id: "newest_to_oldest", title: "Date Added (newest to oldest)" },
        { id: "oldest_to_newest", title: "Date Added (oldest to newest)" },
        { id: "title_a_z", title: "Title (A-Z)" },
        { id: "title_z_a", title: "Title (Z-A)" },
        { id: "author_a_z", title: "Author (A-Z)" },
        { id: "author_z_a", title: "Author (Z-A)" },
      ],
      filterBlock: {
        list: "year_press_releases",
        type: "collection",
      },
      cardData: {
        latest_id: 0,
        sort: "newest_to_oldest",
        tags: "",
        year: "",
        page: 1,
      },
      showLoadMoreButton: true,
      loading: false,
    };
  },
  head() {
    return useSeoHead({
      title: "Press Releases browngrotta arts",
      description: "Description Press Releases browngrotta arts",
      image: "/img/icons/favicon-500x500.png",
      routePath: this.$route.path,
    });
  },
  async created() {
    await this.apiGetFiltersBlock(this.filterBlock);
    await this.apiGetLastPressrelease();
    await this.apiGetPressreleasesList({
      latest_id: this.lastPressrelease.id,
      sort: this.cardData.sort,
      page: 1,
    });
    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Media",
        to: "/media",
        clickable: true,
      },
      {
        title: "Press Releases",
        to: "/media/press-releases",
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);
  },

  watch: {
    tagsData: {
      handler: function (val, oldVal) {
        let year = "";
        val.forEach(el => {
          if (el.selected) {
            year = el.key === "" ? "" : el.key;
          }
        });

        this.cardData.year = year;
        this.selectTypes(year);
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters("publicapi/pressreleases", {
      lastPressrelease: "getLastPressrelease",
      hasPressreleases: "getHasPressreleases",
      pressreleasesList: "getPressreleasesList",
      hasMorePages: "getHasMorePages",
      tagsData: "getFilters",
    }),

    getVisibleTags() {
      return this.tagsData.slice(0, 5);
    },

    getHiddenTags() {
      return this.tagsData.slice(5, this.tagsData.length);
    },
  },

  methods: {
    ...mapActions("publicapi/pressreleases", [
      "apiGetLastPressrelease",
      "apiGetPressreleasesList",
      "apiGetFiltersBlock",
    ]),
    ...mapMutations("publicapi/pressreleases", ["changeFilterValue"]),
    ...mapMutations(["setBreadcrumbs"]),

    getParentClientRect() {
      this.tagsClientRect = this.$refs.titleBox.getBoundingClientRect();
    },

    async sortPressreleases(sort) {
      let data = { ...this.cardData, latest_id: this.lastPressrelease.id, sort, page: 1 };
      let success = await this.apiGetPressreleasesList(data);
      if (success) {
        this.cardData = { ...data };
      }
    },

    async selectTypes(types) {
      let data = { ...this.cardData, latest_id: this.lastPressrelease.id, page: 1, year: types };
      let success = await this.apiGetPressreleasesList(data);
      if (success) {
        this.cardData = data;
      }
    },

    loadMore() {
      if (this.loading || !this.hasMorePages) return;
      this.loading = true;
      this.cardData.page += 1;
      this.apiGetPressreleasesList({
        latest_id: this.lastPressrelease.id,
        sort: this.cardData.sort,
        page: this.cardData.page,
        tags: this.cardData.tags,
      });
      this.loading = false;
    },
    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 18px;
  line-height: 1.3;
  @media screen and (max-width: $xs) {
    font-size: 16px;
    line-height: 1.5;
  }
}

.press-releases {
  display: flex;

  &__header {
    &-background {
      @include backgroundBlock();
    }
  }

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__main-container {
    @include mainContainerCenteredCol(41.8rem 10.5rem 30rem);

    @media screen and (max-width: $xs) {
      padding: 33.5rem 2.6rem 30rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 70%;
    @media screen and (max-width: $md) {
      width: 100%;
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      @media screen and (max-width: $xs) {
        font-size: 35px;
      }
    }

    &_data {
      font-size: 16px;
      line-height: 1.56;
      margin-bottom: 2.9rem;
      text-transform: uppercase;
      @media screen and (max-width: $xs) {
        font-size: 14px;
        margin-bottom: 1.8rem;
      }
    }

    .button {
      margin-top: 30px;
      min-width: 160px;
    }
  }

  &__title-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    margin: 20.2rem 0rem 6.5rem;
    @media screen and (max-width: $xs) {
      margin: 11rem 0rem 4rem;
    }
    &_right {
      @include rightActionsWrapper(false);
    }
    &_title {
      font-size: 40px;
      line-height: 1.25;
      @media screen and (max-width: $xs) {
        font-size: 35px;
        margin-bottom: 30px;
      }
    }
  }
  &__empty {
    @include emptyMessage();
  }

  &__button-box {
    @include centeredButtonBox(8.6rem);
  }
}
</style>

